import type { SessionInfo } from '@opencode/client/promise';

/**
 * cubic's archive of a session on this computer, kept in the session's own record in OpenCode.
 * OpenCode 2.0.26 has `time.archived` but no API that writes it, so the archive is a mark in the
 * session's metadata, which `PATCH /api/session/:id` replaces whole.
 *
 * OpenCode copies a parent's metadata into each fork and child it creates, so the mark names the
 * session it archives, and a copy, which names another session, reads as not archived.
 *
 * Its imports are types only: the session's row reads `archivedAt` in the plain browser bundle,
 * which must not load OpenCode's client.
 */
import type { OpenCodeCall } from './opencode-client';

const ARCHIVE_KEY = 'cubic.archived';

export function archivedAt(session: Pick<SessionInfo, 'id' | 'metadata'>): number | null {
  const mark = session.metadata?.[ARCHIVE_KEY];
  if (typeof mark !== 'object' || mark === null || Array.isArray(mark)) {
    return null;
  }
  return mark.session === session.id && typeof mark.at === 'number' ? mark.at : null;
}

export async function saveArchived(
  send: OpenCodeCall,
  sessionId: string,
  at: number | null,
): Promise<SessionInfo> {
  const session = await send((client, options) =>
    client.session.get({ sessionID: sessionId }, options),
  );
  if ((archivedAt(session) !== null) === (at !== null)) {
    return session;
  }
  const metadata: NonNullable<SessionInfo['metadata']> = Object.fromEntries(
    Object.entries(session.metadata ?? {}).filter(([key]) => key !== ARCHIVE_KEY),
  );
  if (at !== null) {
    metadata[ARCHIVE_KEY] = { session: sessionId, at };
  }
  await send((client, options) =>
    client.session.update({ sessionID: sessionId, metadata }, options),
  );
  return send((client, options) => client.session.get({ sessionID: sessionId }, options));
}
